import type { IPagingInfo } from "@frui.ts/data";
import { excludeEmptyValues, serializeFilters } from "../utils/filters";
import RepositoryBase from "./repositoryBase";
import type BackOfficeConnector from "data/backOfficeConnector";
import type EventBus from "services/eventBus";
import CustomerRefundCandidateDetailResponse from "entities/customerRefundCandidateDetailResponse";
import CustomerRefundCandidateResponse from "entities/customerRefundCandidateResponse";
import type CustomerRefundCandidateSearchRequest from "entities/customerRefundCandidateSearchRequest";
import type CustomerRefundCandidateRejectionRequest from "entities/customerRefundCandidateRejectionRequest";
import type CustomerRefundCandidateUpdateRequest from "entities/customerRefundCandidateUpdateRequest";
import type CustomerRefundCandidateRefundsRequest from "entities/customerRefundCandidateRefundsRequest";

export default class CustomerRefundCandidatesRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getAll(paging: IPagingInfo, filters: CustomerRefundCandidateSearchRequest) {
    return this.callApi(api =>
      api
        .all("finance")
        .all("customer_refund_candidate_search")
        .postPagedEntities(serializeFilters({ ...filters, ...paging }, false), CustomerRefundCandidateResponse)
    );
  }

  get(candidateId: string) {
    return this.callApi(api =>
      api.all("finance").one("customer_refund_candidates", candidateId).getEntity(CustomerRefundCandidateDetailResponse)
    );
  }

  generateRefund(candidateId: string) {
    return this.callApi(api => api.all("finance").one("customer_refund_candidates", candidateId).all("refunds").postOnly({}));
  }

  generateAll(request: CustomerRefundCandidateRefundsRequest) {
    return this.callApi(api => api.all("finance").all("customer_refund_candidates").all("refunds").postOnly(request));
  }

  rejection(candidateId: string, request: CustomerRefundCandidateRejectionRequest) {
    return this.callApi(api =>
      api.all("finance").one("customer_refund_candidates", candidateId).all("rejections").postOnly(request)
    );
  }

  update(candidateId: string, request: CustomerRefundCandidateUpdateRequest) {
    if (request?.refundPayment?.counterpartyBank?.bankAccount && !request.refundPayment?.counterpartyBank?.bankAccount.bank) {
      request.refundPayment.counterpartyBank.bankAccount.bank = undefined;
    }
    return this.callApi(api =>
      api
        .all("finance")
        .one("customer_refund_candidates", candidateId)
        .putEntity(excludeEmptyValues(request), CustomerRefundCandidateDetailResponse)
    );
  }
}
